import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Title } from 'chart.js';
import OrgContext from '../context/OrgContext';

// Register Chart.js components
ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Title);

const OrderStats = () => {
    const { activeOrg } = useContext(OrgContext);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [locationID, setLocationID] = useState('');
    const [productID, setProductID] = useState('');
    const [locations, setLocations] = useState([]);
    const [products, setProducts] = useState([]);
    const [stats, setStats] = useState(null);


    const token = sessionStorage.getItem('token');


    useEffect(() => {
        fetchLocations();
        fetchProducts();
    }, [activeOrg]);

    const fetchLocations = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/locations`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: activeOrg._id // ensure activeOrg._id contains the correct organization ID
                },
            });
            setLocations(response.data);
        } catch (error) {
            toast.error('Error fetching locations.');
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/products`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    orgid: activeOrg._id // ensure activeOrg._id contains the correct organization ID
                },
            });
            setProducts(response.data);
        } catch (error) {
            toast.error('Error fetching products.');
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SRV}/stats`, {
                headers: { Authorization: `Bearer ${token}`, orgid: activeOrg._id },
                params: {
                    locationID,
                    productID,
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                },
            });
            setStats(response.data);
        } catch (error) {
            toast.error('Error fetching statistics.');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        fetchStats();
    };

    const ordersByHourData = stats
        ? {
            labels: Object.keys(stats.ordersByHour),
            datasets: [
                {
                    label: 'Orders by Time of Day',
                    data: Object.values(stats.ordersByHour),
                    backgroundColor: '#ffce56',
                },
            ],
        }
        : null;

    const ordersByDayData = stats
        ? {
            labels: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            datasets: [
                {
                    label: 'Orders by Week',
                    data: Object.values(stats.ordersByDayOfWeek),
                    backgroundColor: '#36a2eb',
                },
            ],
        }
        : null;

    return (
        <div>
            <div className="bg-gray-800 p-4 shadow-lg rounded-lg mb-4">
                <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4">
                    {/* Start Date */}
                    <div>
                        <label className="block text-white mb-2">Start Date</label>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="yyyy/MM/dd"
                            className="px-4 py-2 bg-gray-700 text-white rounded"
                        />
                    </div>

                    {/* End Date */}
                    <div>
                        <label className="block text-white mb-2">End Date</label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            dateFormat="yyyy/MM/dd"
                            className="px-4 py-2 bg-gray-700 text-white rounded"
                        />
                    </div>

                    {/* Location */}
                    <div>
                        <label className="block text-white mb-2">Location</label>
                        <select
                            value={locationID}
                            onChange={(e) => setLocationID(e.target.value)}
                            className="px-4 py-2 bg-gray-700 text-white rounded"
                        >
                            <option value="">All Locations</option>
                            {locations.map(location => (
                                <option key={location._id} value={location._id}>
                                    {location.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Product Filter */}
                    <div>
                        <label className="block text-white mb-2">Product</label>
                        <select
                            value={productID}
                            onChange={(e) => setProductID(e.target.value)}
                            className="px-4 py-2 bg-gray-700 text-white rounded"
                        >
                            <option value="">All Products</option>
                            {products.map(product => (
                                <option key={product._id} value={product._id}>
                                    {product.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Submit Button */}
                    <div className="self-end">
                        <button
                            type="submit"
                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                            View
                        </button>
                    </div>
                </form>
            </div>

            {stats && (
                <div>
                    {/* Line 1: Cost, Revenue, Profit */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div className="bg-gray-800 p-4 rounded-lg shadow-md">
                            <h2 className="text-lg font-bold">Total Cost</h2>
                            <p>{stats.totalCost} kr</p>
                        </div>
                        <div className="bg-gray-800 p-4 rounded-lg shadow-md">
                            <h2 className="text-lg font-bold">Total Revenue</h2>
                            <p>{stats.totalRevenue} kr</p>
                        </div>
                        <div className="bg-gray-800 p-4 rounded-lg shadow-md">
                            <h2 className="text-lg font-bold">Total Profit</h2>
                            <p>{stats.totalProfit} kr</p>
                        </div>
                    </div>

                    {/* Line 2: Average Order Value, Most Common Order Type */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-gray-800 p-4 rounded-lg shadow-md">
                            <h2 className="text-lg font-bold">Average Order Value</h2>
                            <p>{stats.averageOrderValue} kr</p>
                        </div>
                        <div className="bg-gray-800 p-4 rounded-lg shadow-md">
                            <h2 className="text-lg font-bold">Most Common Order Type</h2>
                            <p>{stats.mostCommonOrderType}</p>
                        </div>
                    </div>

                    {/* Line 3: Average Time to Ready, Average Time to Served */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-gray-800 p-4 rounded-lg shadow-md">
                            <h2 className="text-lg font-bold">Average Time to Ready</h2>
                            <p>{stats.averageTimeToReady} mins</p>
                        </div>
                        <div className="bg-gray-800 p-4 rounded-lg shadow-md">
                            <h2 className="text-lg font-bold">Average Time to Served</h2>
                            <p>{stats.averageTimeToServed} mins</p>
                        </div>
                    </div>

                    {/* Line 4: Orders by Week, Orders by Time of Day */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-gray-800 p-4 rounded-lg shadow-md">
                            <h2 className="text-lg font-bold">Orders by Week</h2>
                            <Bar data={ordersByDayData} />
                        </div>
                        <div className="bg-gray-800 p-4 rounded-lg shadow-md">
                            <h2 className="text-lg font-bold">Orders by Time of Day</h2>
                            <Line data={ordersByHourData} />
                        </div>
                    </div>

                    {/* Line 5: Top 5 Profitable Products */}
                    <div className="mb-6">
                        <h2 className="text-xl font-bold">Top 5 Products by profit</h2>
                        <ul>
                            <ul>
                                {stats.topProductsByProfit.map((product, index) => (
                                    <li key={index} className="bg-gray-800 p-4 rounded-lg shadow-md mb-2">
                                        {product.productName}: {product.totalProfit ? product.totalProfit.toFixed(1) : '0'} {activeOrg.valute}
                                    </li>
                                ))}
                            </ul>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderStats;
